import { isSharedViewIdentity, type SavedView } from "@estelle/domain";
import { Check, ChevronsUpDown, Star, Trash2 } from "lucide-react";
import { useState } from "react";

import { can, usePermissions } from "@/auth/permissions";
import { canonicalJson } from "@/components/views/canonical";
import type { ListState } from "@/components/views/SaveViewButton";
import { useSavedViews } from "@/components/views/useSavedViews";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * The bar's `All ⇅` — the section's saved views, default first, in one
 * compact dropdown (#349, `SavedViewsAdmin.tsx` already called it this).
 *
 * Replaces `SavedViewTabs`, which gave the same list a whole row of its own.
 * That row was one of the two the reference bar collapses into one, and a
 * tab strip does not shrink — on a phone it wrapped, or scrolled sideways
 * under content that also wanted the width.
 *
 * `All` is not a saved view; it is the absence of one, so it is not sorted
 * into `views` — it is always the first entry, the way clearing the filter
 * is always reachable regardless of what got saved after it. Which entry is
 * "open" is derived from the list state exactly as the tab row did: the URL
 * is the one thing every control here agrees on, so this holds no selection
 * of its own to drift from it.
 */
export function SavedViewPicker({
  scope,
  savedViews,
  emptyLabel,
  current,
  onSelect,
}: {
  /** Named only for the trigger's `aria-label` — `savedViews` already comes
   * bound to this scope. */
  scope: string;
  /**
   * One `useSavedViews(scope)` call, shared with `SaveViewButton` by the
   * parent `FilterBar` (#355) — picking a view here calls `savedViews.select`
   * so Save reads the same "open view" back, which two separate hook
   * instances could never agree on.
   */
  savedViews: ReturnType<typeof useSavedViews>;
  /** What the unfiltered list is called here — "All orders". */
  emptyLabel: string;
  /** What the list is showing, to work out which view is open. */
  current: ListState;
  /** Null means the empty entry. The caller decides what applying one does. */
  onSelect: (view: SavedView | null) => void;
}) {
  const { views, rename, setDefault, remove, select } = savedViews;
  // Views are shared with everyone (#633): anyone who can open the list can
  // add one, but changing what others rely on — rename, default, delete —
  // needs `settings.views.manage`, as the server enforces.
  const canManage = can(usePermissions(), "settings.views.manage");
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  // Canonical, so a view read back from `jsonb` (which reorders keys) still
  // matches the same filter decoded from the URL (#527).
  const serialised = canonicalJson(current);
  const openView =
    views.find(
      (view) =>
        canonicalJson({
          filter: view.filter,
          columns: view.columns,
          sort: view.sort,
        }) === serialised,
    ) ?? null;

  // Default first, everyone else in the order the list already returned
  // them — `useSavedViews` does not otherwise promise an order, so nothing
  // here relies on it beyond "the default is somewhere in it".
  const ordered = [...views].sort(
    (a, b) => Number(b.isDefault) - Number(a.isDefault),
  );

  /** A refusal is shown, never swallowed — same contract as the old tabs. */
  function handle(result: { ok: boolean; rejection?: { message: string } }) {
    setProblem(result.ok ? null : (result.rejection?.message ?? "Refused."));
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setRenaming(null);
          setProblem(null);
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 gap-1.5 pr-2"
          aria-label={`Saved views for ${scope}`}
        >
          <span className="max-w-32 truncate">
            {openView ? openView.name : emptyLabel}
          </span>
          <ChevronsUpDown
            className="text-muted-foreground size-3.5"
            aria-hidden
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuItem
          onSelect={() => {
            select(null);
            onSelect(null);
          }}
        >
          {emptyLabel}
          {openView === null ? <Check className="ml-auto size-4" /> : null}
        </DropdownMenuItem>

        {ordered.length > 0 ? <DropdownMenuSeparator /> : null}

        {ordered.map((view) => {
          const isOpen = view.id === openView?.id;

          if (renaming === view.id) {
            return (
              <div key={view.id} className="px-2 py-1.5">
                <Input
                  autoFocus
                  defaultValue={view.name}
                  className="h-7 text-sm"
                  onKeyDown={(event) => {
                    // The dropdown's own typeahead would otherwise treat
                    // every keystroke here as "jump to the item starting
                    // with this letter" instead of text entry.
                    event.stopPropagation();
                    if (event.key === "Enter") {
                      void rename(view.id, event.currentTarget.value).then(
                        handle,
                      );
                      setRenaming(null);
                    }
                    if (event.key === "Escape") setRenaming(null);
                  }}
                  onBlur={() => {
                    setRenaming(null);
                  }}
                />
              </div>
            );
          }

          return (
            <div key={view.id} className="flex items-center">
              <DropdownMenuItem
                className="flex-1"
                onSelect={() => {
                  select(view.id);
                  onSelect(view);
                }}
              >
                <span className="truncate">{view.name}</span>
                {view.isDefault ? (
                  <Star
                    className="ml-1 size-3 shrink-0 fill-current"
                    aria-hidden
                  />
                ) : null}
                {isOpen ? <Check className="ml-auto size-4 shrink-0" /> : null}
              </DropdownMenuItem>

              {canManage ? (
                <DropdownMenuSub>
                  {/* Its own chevron comes from `DropdownMenuSubTrigger` — an
                    icon of ours here would just draw a second one beside it. */}
                  <DropdownMenuSubTrigger
                    className="mr-1 w-auto"
                    aria-label={`Manage ${view.name}`}
                  />
                  <DropdownMenuSubContent>
                    {/* The views that come with shipment tracking are found by
                      their name (#527): neither is offered for them, and the
                      server refuses both whatever this shows. */}
                    {isSharedViewIdentity(view.scope, view.name) ? null : (
                      <DropdownMenuItem
                        onSelect={(event) => {
                          // Keep the menu open: choosing an item closes it, and
                          // closing it cleared `renaming` again before the box
                          // ever showed, so Rename did nothing (#553).
                          event.preventDefault();
                          setRenaming(view.id);
                        }}
                      >
                        Rename
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onSelect={() => {
                        void setDefault(view.id).then(handle);
                      }}
                    >
                      <Star
                        className={cn(view.isDefault && "fill-current")}
                        aria-hidden
                      />
                      {view.isDefault
                        ? "Default for this list"
                        : "Make it the default"}
                    </DropdownMenuItem>
                    {isSharedViewIdentity(view.scope, view.name) ? null : (
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => {
                          void remove(view.id).then(handle);
                          // The list is still showing this view's rows, so
                          // leaving it open would underline an entry that no
                          // longer exists. Clearing is the honest landing.
                          select(null);
                          onSelect(null);
                        }}
                      >
                        <Trash2 aria-hidden />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              ) : null}
            </div>
          );
        })}

        {problem ? (
          <p className="text-destructive px-2 py-1 text-xs">{problem}</p>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
